import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton, Tooltip, Typography } from "@mui/material";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import React, { useState } from "react";

export interface PrescriptionItem {
  id: string;
  productName: string;
  dosage: string;
  quantity: number;
  unit: string;
  price: number;
}

interface PrescriptionItemsTableProps {
  items: PrescriptionItem[];
  onDelete?: (itemId: string) => void;
}

export const PrescriptionItemsTable: React.FC<PrescriptionItemsTableProps> = ({
  items,
  onDelete,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const columns: CustomTableColumn<PrescriptionItem>[] = [
    {
      id: "productName",
      label: "Product Name",
      getValue: (row) => (
        <Typography variant="body2">{row.productName}</Typography>
      ),
      sortable: true,
      sortAccessor: (row) => row.productName,
    },
    {
      id: "dosage",
      label: "Dosage",
      getValue: (row) => <Typography variant="body2">{row.dosage}</Typography>,
      sortable: true,
      sortAccessor: (row) => row.dosage,
    },
    {
      id: "quantity",
      label: "Qty",
      getValue: (row) => (
        <Typography variant="body2">{row.quantity}</Typography>
      ),
      sortable: true,
      sortAccessor: (row) => row.quantity,
    },
    {
      id: "unit",
      label: "Unit",
      getValue: (row) => <Typography variant="body2">{row.unit}</Typography>,
      sortable: true,
      sortAccessor: (row) => row.unit,
    },
    {
      id: "price",
      label: "Price",
      getValue: (row) => (
        <Typography variant="body2">{formatPrice(row.price)}</Typography>
      ),
      align: "right",
      sortable: true,
      sortAccessor: (row) => row.price,
    },
    {
      id: "actions",
      label: "",
      getValue: (row) =>
        onDelete ? (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(row.id)}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
      align: "center",
      width: 80,
    },
  ];

  return (
    <CustomTable
      data={items}
      columns={columns}
      totalCount={items.length}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      isLoading={false}
      minHeight={300}
    />
  );
};
